import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, query, where, orderBy, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../admin/firebase/config';
import { useWaiterAuth } from '../context/WaiterAuthContext';
import { useOutlet } from '../../context/OutletContext';
import useOutletMenuItems from '../../hooks/useOutletMenuItems';
import { useTerminal } from '../context/TerminalContext';
import WaiterSidebar from '../components/WaiterSidebar';
import TableSelectionModal from '../components/TableSelectionModal';
import KOTComponent from '../components/KOTComponent';
import CustomizableBill from '../components/CustomizableBill';
import './OrderLinePetpooja.css';

const OrderLinePetpooja = () => {
  const { currentWaiter } = useWaiterAuth();
  const { selectedOutlet } = useOutlet();
  const { currentTerminal } = useTerminal();
  const { menuData, loading: menuLoading } = useOutletMenuItems(selectedOutlet?.id);

  // State
  const [orders, setOrders] = useState([]);
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [peopleCount, setPeopleCount] = useState(2);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [showTableModal, setShowTableModal] = useState(false);
  const [showKOTModal, setShowKOTModal] = useState(false);
  const [showBillModal, setShowBillModal] = useState(false);
  const [printAction, setPrintAction] = useState(null);

  // Subscribe to orders
  useEffect(() => {
    if (!selectedOutlet) return;

    const q = query(
      collection(db, 'orders'),
      where('outletId', '==', selectedOutlet.id),
      where('orderType', '==', 'dine-in'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const ordersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setOrders(ordersData);
    });

    return () => unsubscribe();
  }, [selectedOutlet]);

  // Subscribe to tables
  useEffect(() => {
    if (!selectedOutlet) return;

    const q = query(
      collection(db, 'tables'),
      where('outletId', '==', selectedOutlet.id)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const tablesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTables(tablesData);
    });

    return () => unsubscribe();
  }, [selectedOutlet]);

  // Get all menu items
  const getAllMenuItems = () => {
    if (!menuData || !menuData.categories) return [];
    const items = [];
    menuData.categories.forEach(category => {
      category.sections?.forEach(section => {
        section.subsections?.forEach(subsection => {
          subsection.veg?.items?.forEach(item => {
            items.push({
              ...item,
              category: category.category,
              isVeg: true,
              id: item.id || `${category.category}-${item.name}`
            });
          });
          subsection.nonVeg?.items?.forEach(item => {
            items.push({
              ...item,
              category: category.category,
              isVeg: false,
              id: item.id || `${category.category}-${item.name}`
            });
          });
        });
      });
    });
    return items;
  };

  // Filter menu items
  const getFilteredMenuItems = () => {
    let allItems = getAllMenuItems();

    if (activeCategory !== 'all') {
      allItems = allItems.filter(item =>
        item.category.toLowerCase() === activeCategory.toLowerCase()
      );
    }

    if (searchQuery.trim()) {
      const search = searchQuery.toLowerCase();
      allItems = allItems.filter(item =>
        item.name.toLowerCase().includes(search) ||
        item.category.toLowerCase().includes(search)
      );
    }

    return allItems;
  };

  // Get categories
  const getCategories = () => {
    if (!menuData || !menuData.categories) return [];
    const allItems = getAllMenuItems();

    return menuData.categories.map(cat => ({
      id: cat.category.toLowerCase(),
      name: cat.category,
      icon: getCategoryIcon(cat.category),
      count: allItems.filter(item => item.category === cat.category).length
    }));
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Special': '⭐',
      'Soups': '🍲',
      'Desserts': '🍰',
      'Chickens': '🍗',
      'Chicken': '🍗',
      'Biryani': '🍛',
      'Rice': '🍚',
      'Starters': '🍢',
      'Pujabi': '🍛',
      'Pizza': '🍕',
      'Pasta': '🍝'
    };
    return icons[category] || '🍽️';
  };

  // Filter orders by tab
  const getFilteredOrders = () => {
    if (activeTab === 'all') return orders;
    if (activeTab === 'dine-in') return orders.filter(o => o.orderType === 'dine-in');
    if (activeTab === 'wait-list') return orders.filter(o => o.status === 'pending');
    if (activeTab === 'take-away') return orders.filter(o => o.orderType === 'takeaway');
    if (activeTab === 'served') return orders.filter(o => o.status === 'delivered' || o.status === 'completed');
    return orders;
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const badges = {
      'pending': { text: 'Wait List', color: '#FF9800', bg: '#FFF3E0' },
      'confirmed': { text: 'In Kitchen', color: '#4CAF50', bg: '#E8F5E9' },
      'preparing': { text: 'In Kitchen', color: '#4CAF50', bg: '#E8F5E9' },
      'ready': { text: 'Ready', color: '#9C27B0', bg: '#F3E5F5' },
      'delivered': { text: 'Served', color: '#2196F3', bg: '#E3F2FD' },
      'completed': { text: 'Served', color: '#2196F3', bg: '#E3F2FD' }
    };
    return badges[status] || { text: status, color: '#9E9E9E', bg: '#F5F5F5' };
  };

  // Order counts
  const counts = {
    all: orders.length,
    dineIn: orders.filter(o => o.orderType === 'dine-in').length,
    waitList: orders.filter(o => o.status === 'pending').length,
    takeAway: orders.filter(o => o.orderType === 'takeaway').length,
    served: orders.filter(o => o.status === 'delivered' || o.status === 'completed').length
  };

  // Item quantity in cart
  const getItemQuantity = (itemId) => {
    const item = orderItems.find(i => i.id === itemId);
    return item ? item.quantity : 0;
  };

  // Add item to cart
  const addItemToCart = (item) => {
    const existingIndex = orderItems.findIndex(i => i.id === item.id);
    if (existingIndex > -1) {
      const updated = [...orderItems];
      updated[existingIndex].quantity += 1;
      setOrderItems(updated);
    } else {
      setOrderItems([...orderItems, { ...item, quantity: 1 }]);
    }
  };

  // Update quantity
  const updateQuantity = (itemId, delta) => {
    setOrderItems(items =>
      items
        .map(item => {
          if (item.id === itemId) {
            return { ...item, quantity: item.quantity + delta };
          }
          return item;
        })
        .filter(item => item.quantity > 0)
    );
  };

  // Calculate totals
  const subtotal = orderItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const tax = subtotal * 0.05;
  const total = subtotal + tax;

  // Place order
  const handlePlaceOrder = async () => {
    if (!selectedTable || orderItems.length === 0) {
      alert('Please select a table and add items');
      return;
    }

    try {
      const orderData = {
        outletId: selectedOutlet.id,
        outletName: selectedOutlet.name,
        tableId: selectedTable.id,
        tableNumber: selectedTable.number,
        orderType: 'dine-in',
        items: orderItems,
        peopleCount,
        subtotal,
        tax,
        total,
        status: 'pending',
        paymentMethod,
        waiterId: currentWaiter.uid,
        terminalId: currentTerminal?.id || null,
        terminalNumber: currentTerminal?.terminalNumber || null,
        waiterName: currentWaiter.name,
        createdAt: serverTimestamp(),
        orderNumber: `F${Date.now().toString().slice(-4)}`
      };

      await addDoc(collection(db, 'orders'), orderData);

      // Reset
      setOrderItems([]);
      setSelectedTable(null);
      setPeopleCount(2);
      alert('✅ Order placed successfully!');
    } catch (error) {
      console.error('Error placing order:', error);
      alert('❌ Error placing order');
    }
  };

  // Time ago helper
  const getTimeAgo = (timestamp) => {
    if (!timestamp) return 'Just now';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} mins ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours} hours ago`;
  };

  // Print handler
  const handlePrint = () => {
    if (!selectedTable || orderItems.length === 0) {
      alert('Please select a table and add items');
      return;
    }

    // Show print menu
    const choice = window.confirm('Print KOT? (OK for KOT, Cancel for Bill)');
    if (choice) {
      setPrintAction('KOT');
      setShowKOTModal(true);
    } else {
      setPrintAction('Bill');
      setShowBillModal(true);
    }
  };

  if (!selectedOutlet) {
    return (
      <div className="orderline-petpooja">
        <WaiterSidebar currentWaiter={currentWaiter} />
        <div className="orderline-loading">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const filteredOrders = getFilteredOrders();
  const menuItems = getFilteredMenuItems();
  const categories = getCategories();

  return (
    <div className="orderline-petpooja">
      <WaiterSidebar currentWaiter={currentWaiter} />

      <div className="orderline-main">
        {/* Header */}
        <header className="orderline-header">
          <div className="header-left">
            <h1 className="header-title">Order Line</h1>
          </div>
          <div className="header-center">
            <div className="search-box">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                placeholder="Search menu, orders and more"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>
          </div>
          <div className="header-right">
            <button className="notif-btn">
              <span className="notif-icon">🔔</span>
            </button>
            <div className="user-profile">
              <div className="user-avatar">
                <span>{currentWaiter?.name?.charAt(0) || 'W'}</span>
              </div>
              <div className="user-details">
                <span className="user-name">{currentWaiter?.name || 'Waiter'}</span>
                <span className="user-role">Waiter</span>
              </div>
            </div>
          </div>
        </header>

        {/* Tabs */}
        <div className="orderline-tabs">
          {[
            { key: 'all', label: 'All', count: counts.all },
            { key: 'dine-in', label: 'Dine In', count: counts.dineIn },
            { key: 'wait-list', label: 'Wait List', count: counts.waitList },
            { key: 'take-away', label: 'Take Away', count: counts.takeAway },
            { key: 'served', label: 'Served', count: counts.served }
          ].map(tab => (
            <button
              key={tab.key}
              className={`tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label} <span className="tab-count">{tab.count}</span>
            </button>
          ))}
        </div>

        {/* Orders Horizontal Scroll */}
        <div className="orders-scroll-container">
          <div className="orders-scroll">
            {filteredOrders.map(order => {
              const badge = getStatusBadge(order.status);
              return (
                <div
                  key={order.id}
                  className="order-card-h"
                  onClick={() => {
                    setCurrentOrder(order);
                    setSelectedTable(tables.find(t => t.id === order.tableId));
                    setOrderItems(order.items || []);
                  }}
                >
                  <div className="order-card-top">
                    <span className="order-num">{order.orderNumber}</span>
                    <span className="table-num">Table {order.tableNumber}</span>
                  </div>
                  <div className="order-card-mid">
                    <span className="order-items">Item: {order.items?.length || 0}X</span>
                    <span
                      className="order-status"
                      style={{ backgroundColor: badge.bg, color: badge.color }}
                    >
                      {badge.text}
                    </span>
                  </div>
                  <div className="order-card-bottom">
                    <span className="order-time">{getTimeAgo(order.createdAt)}</span>
                  </div>
                </div>
              );
            })}
            {filteredOrders.length === 0 && (
              <div className="no-orders">No orders to display</div>
            )}
          </div>
        </div>

        {/* Menu Section */}
        <div className="menu-section">
          <h2 className="menu-title">Foodies Menu</h2>

          {/* Category Tabs */}
          <div className="category-tabs">
            <button
              className={`cat-tab ${activeCategory === 'all' ? 'active' : ''}`}
              onClick={() => setActiveCategory('all')}
            >
              <span className="cat-icon">🍽️</span>
              <div className="cat-content">
                <span className="cat-name">All Menu</span>
                <span className="cat-count">{getAllMenuItems().length} items</span>
              </div>
            </button>
            {categories.map(cat => (
              <button
                key={cat.id}
                className={`cat-tab ${activeCategory === cat.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat.id)}
              >
                <span className="cat-icon">{cat.icon}</span>
                <div className="cat-content">
                  <span className="cat-name">{cat.name}</span>
                  <span className="cat-count">{cat.count} items</span>
                </div>
              </button>
            ))}
          </div>

          {/* Menu Items Grid */}
          <div className="menu-grid">
            {menuItems.map(item => {
              const qty = getItemQuantity(item.id);
              return (
                <div key={item.id} className="menu-item">
                  <div className="menu-item-img">
                    {item.image ? (
                      <img src={item.image} alt={item.name} />
                    ) : (
                      <div className="menu-item-placeholder">🍽️</div>
                    )}
                    <span className="menu-item-cat">{item.category}</span>
                  </div>
                  <div className="menu-item-body">
                    <h4 className="menu-item-name">{item.name}</h4>
                    <div className="menu-item-footer">
                      <span className="menu-item-price">₹{item.price}</span>
                      <div className="menu-item-controls">
                        {qty > 0 && (
                          <>
                            <button
                              className="qty-btn minus"
                              onClick={() => updateQuantity(item.id, -1)}
                            >
                              −
                            </button>
                            <span className="qty-value">{qty}</span>
                          </>
                        )}
                        <button
                          className="qty-btn plus"
                          onClick={() => {
                            if (qty === 0) {
                              addItemToCart(item);
                            } else {
                              updateQuantity(item.id, 1);
                            }
                          }}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {menuItems.length === 0 && (
              <div className="no-menu-items">
                <p>No menu items found</p>
                {searchQuery && <small>Try a different search term</small>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <aside className="orderline-sidebar">
        <div className="sidebar-header">
          <div className="table-info">
            <h3 className="table-title">
              Table No #{selectedTable?.number || '---'}
            </h3>
            <button
              className="select-table-btn"
              onClick={() => setShowTableModal(true)}
            >
              {selectedTable ? 'Change Table' : 'Select Table'}
            </button>
          </div>
          <div className="order-details">
            <span className="order-num-detail">
              Order #{currentOrder?.orderNumber || 'New Order'}
            </span>
            <span className="people-count">
              <input
                type="number"
                min="1"
                value={peopleCount}
                onChange={(e) => setPeopleCount(parseInt(e.target.value) || 1)}
                className="people-input"
              /> People
            </span>
          </div>
        </div>

        <div className="sidebar-items">
          <div className="items-header">
            <span>Ordered Items</span>
            <span className="items-badge">{orderItems.length.toString().padStart(2, '0')}</span>
          </div>
          <div className="items-list">
            {orderItems.length > 0 ? (
              orderItems.map((item, idx) => (
                <div key={idx} className="cart-item">
                  <div className="cart-item-left">
                    <span className="cart-qty">{item.quantity}x</span>
                    <span className="cart-name">{item.name}</span>
                  </div>
                  <span className="cart-price">₹{(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))
            ) : (
              <div className="empty-cart">
                <p>No items added</p>
                <small>Select items from menu</small>
              </div>
            )}
          </div>
        </div>

        <div className="sidebar-summary">
          <h4>Payment Summary</h4>
          <div className="summary-row">
            <span>Subtotal</span>
            <span>₹{subtotal.toFixed(2)}</span>
          </div>
          <div className="summary-row">
            <span>Tax (5%)</span>
            <span>₹{tax.toFixed(2)}</span>
          </div>
          <div className="summary-row total-row">
            <span>Total Payable</span>
            <span>₹{total.toFixed(2)}</span>
          </div>
        </div>

        <div className="sidebar-payment">
          <h4>Payment Method</h4>
          <div className="payment-methods">
            {[
              { key: 'cash', icon: '💵', label: 'Cash' },
              { key: 'card', icon: '💳', label: 'Card' },
              { key: 'scan', icon: '📱', label: 'Scan' }
            ].map(method => (
              <button
                key={method.key}
                className={`payment-method ${paymentMethod === method.key ? 'active' : ''}`}
                onClick={() => setPaymentMethod(method.key)}
              >
                <span>{method.icon}</span>
                <span>{method.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="sidebar-actions">
          <button
            className="action-btn print-btn"
            onClick={handlePrint}
            disabled={!selectedTable || orderItems.length === 0}
          >
            <span>🖨️</span> Print
          </button>
          <button
            className="action-btn place-btn"
            onClick={handlePlaceOrder}
            disabled={!selectedTable || orderItems.length === 0}
          >
            <span>📦</span> Place Order
          </button>
        </div>
      </aside>

      {/* Table Selection Modal */}
      {showTableModal && (
        <TableSelectionModal
          tables={tables}
          selectedTable={selectedTable}
          onSelect={(table) => {
            setSelectedTable(table);
            setShowTableModal(false);
          }}
          onClose={() => setShowTableModal(false)}
        />
      )}

      {/* KOT Print Modal */}
      {showKOTModal && (
        <div className="print-modal-overlay" onClick={() => setShowKOTModal(false)}>
          <div className="print-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowKOTModal(false)}>×</button>
            <KOTComponent
              orderData={{
                orderNumber: currentOrder?.orderNumber || `F${Date.now().toString().slice(-4)}`,
                tableNumber: selectedTable?.number,
                items: orderItems,
                waiterName: currentWaiter?.name,
                createdAt: new Date()
              }}
              outletData={selectedOutlet}
            />
          </div>
        </div>
      )}

      {/* Bill Print Modal */}
      {showBillModal && (
        <div className="print-modal-overlay" onClick={() => setShowBillModal(false)}>
          <div className="print-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowBillModal(false)}>×</button>
            <CustomizableBill
              orderData={{
                orderNumber: currentOrder?.orderNumber || `F${Date.now().toString().slice(-4)}`,
                tableNumber: selectedTable?.number,
                items: orderItems,
                subtotal,
                tax,
                total,
                paymentMethod,
                createdAt: new Date()
              }}
              outletData={selectedOutlet}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderLinePetpooja;
